import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import Alert from "../../components/Core/Alert/Alert";
import AddButton from "../../components/Core/AddButton/AddButton";
import AppInputNew from "../../components/Core/AppInput/AppInputNew";
import ButtonNew from "../../components/Core/Button/ButtonNew";
import { FormCard } from "../../components/Core/FormCard";
import Breadcrumbs, { BreadcrumbItem } from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import ListActionsMenu, {
	ListToolbarAction,
} from "../../components/Shared/ListPageToolbar/ListActionsMenu";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import {
	CustomerBreadcrumbs,
	CustomerUsersListBreadcrumbs,
} from "../../configs/breadcrumbNavigation";
import { routes } from "../../configs/constants/routes";
import { FEEDBACK_MESSAGE_TYPE } from "../../configs/feedbackMessages";
import Customer from "../../models/Customer";
import CustomerUser, { UpdateCustomerUserRequest } from "../../models/CustomerUser";
import UserCustomers, { NotificationSettings } from "../../models/UserCustomers";
import { toRelationPayload } from "../../utils/customerUser.utils";
import {
	useCreateCustomerUser,
	useCustomer,
	useCustomersByCustomerUser,
	useCustomerUser,
	useCustomerUsers,
	useDeleteCustomerUser,
	useUpdateCustomerUser,
} from "../../networking/customer.hooks";
import { useInviteUser } from "../../networking/internal-user.hooks";
import phoneNumberSchema from "../../schemas/phoneNumberSchema";
import { ConfirmModalProps, useConfirmModalStore } from "../../stores/confirmModal.store";
import { customerRoute, formatRoute, navigateToCustomerUsers } from "../../utils/route.utils";
import { isValidEmail } from "../../utils/string.utils";
import SelectCustomerDialog from "./SelectCustomer";
import UserCustomerRow from "./UserCustomerRow";
import { InviteOption } from "../../models/InternalUser";
import InvitationModal from "../../components/Shared/InvitationModal";
import SetPasswordModal from "../../components/Shared/SetPasswordModal";
import { toast } from "../../utils/toast";

type Params = {
	customerId: string;
	userId: string;
};

const UserInputsValidationSchema = yup.object().shape({
	profile: yup.object().shape({
		phoneNumber: phoneNumberSchema,
	}),
	firstName: yup.string().required("FIELD_IS_REQUIRED"),
	lastName: yup.string().required("FIELD_IS_REQUIRED"),
	email: yup.string().required("FIELD_IS_REQUIRED").email(),
});

const CustomerUserForm: React.FC = () => {
	const modalStore = useConfirmModalStore();

	const { userId, customerId } = useParams<keyof Params>() as Params;

	const isEdit = !!userId;

	// API hooks
	const { customer, isLoading: isLoadingCustomer } = useCustomer(customerId);
	const { customerUser } = useCustomerUser(userId, isEdit);
	const { customers: userCustomers } = useCustomersByCustomerUser(userId, isEdit);
	const { mutateAsync: updateCustomerUser } = useUpdateCustomerUser();
	const { mutateAsync: createCustomerUser } = useCreateCustomerUser();
	const { mutateAsync: deleteCustomerUser } = useDeleteCustomerUser();
	const { mutateAsync: inviteUser } = useInviteUser();

	const navigate = useNavigate();
	const location = useLocation();

	const { t } = useTranslation();

	// The customer id in the URL only reflects a real navigation context when
	// coming from the customer detail page's Users tab (which sets this flag).
	// Global search and the org-wide users list link via the user's first
	// customer, where a customer crumb would be misleading. Create is always
	// initiated from a customer, so it always keeps the customer context.
	const hasCustomerContext =
		!isEdit || !!(location.state as { fromCustomer?: boolean } | null)?.fromCustomer;

	const [isSelectCustomerOpen, setIsSelectCustomerOpen] = useState(false);
	const [multiCustomers, setMultiCustomers] = useState<UserCustomers[]>([]);
	const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
	const [isSetPasswordModalOpen, setIsSetPasswordModalOpen] = useState(false);
	const [createdNewUser, setCreatedNewUser] = useState<CustomerUser | undefined>();

	const { register, handleSubmit, formState, reset, watch } = useForm<CustomerUser>({
		defaultValues: isEdit ? { ...customerUser } : undefined,
		mode: "onBlur",
		resolver: yupResolver(UserInputsValidationSchema),
	});

	useEffect(() => {
		// Sync form with loaded user, but never clobber in-progress edits (the
		// query refetches whenever a customer relation is saved from the right
		// hand card, which would otherwise reset the form mid-edit).
		if (isEdit && customerUser && !formState.isDirty) {
			reset(customerUser);
		}
	}, [customerUser, isEdit]);

	// Search for customer users with same email
	const { customerUsers = [] } = useCustomerUsers(
		{
			keyword: watch().email?.trim(),
		},
		!!watch().email && isValidEmail(watch().email || "")
	);

	const onSubmit = handleSubmit(async (e) => {
		if (!customer?.id) return;

		const userData: UpdateCustomerUserRequest = {
			firstName: e.firstName,
			lastName: e.lastName,
			email: e.email,
			profile: {
				customers: toRelationPayload(multiCustomers),
				title: e.profile?.title,
				phoneNumber: e.profile?.phoneNumber,
			},
		};

		if (isEdit) {
			const success = await updateCustomerUser({ userId: userId!, update: userData })
				.then(() => true)
				.catch(() => false);

			if (success) {
				toast.success(FEEDBACK_MESSAGE_TYPE.CHANGES_SAVE_SUCCESSFULLY);
				reset(e);
			} else {
				toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
			}
		} else {
			const created = await createCustomerUser(userData).catch(() => undefined);

			if (created) {
				toast.success(FEEDBACK_MESSAGE_TYPE.CHANGES_SAVE_SUCCESSFULLY);
				setCreatedNewUser(created);
				setIsInviteModalOpen(true);
			} else {
				toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
			}
		}
	});

	const navigateToUsersList = () => {
		// Return to where the user came from — the customer's Users tab when
		// navigated from a customer, otherwise the org-wide users list.
		if (!hasCustomerContext) {
			navigate(routes.CUSTOMER_USERS_LIST);
			return;
		}
		const routePath = formatRoute([routes.CUSTOMER_DETAILS, routes.CUSTOMER_USERS].join("/"), [
			customer?.id,
		]);
		navigate(routePath);
	};

	/**
	 * Persist the customer relations right away (edit mode only — on create the
	 * relations are part of the create request instead). Only `profile.customers`
	 * plus the already-persisted profile fields are sent, so unsaved edits in the
	 * profile form are never committed as a side effect.
	 */
	async function persistCustomerRelations(rows: UserCustomers[]) {
		if (!isEdit) return;

		const success = await updateCustomerUser({
			userId: userId!,
			update: {
				profile: {
					customers: toRelationPayload(rows),
					title: customerUser?.profile?.title,
					phoneNumber: customerUser?.profile?.phoneNumber,
				},
			},
		})
			.then(() => true)
			.catch(() => false);

		if (success) {
			toast.success(FEEDBACK_MESSAGE_TYPE.CHANGES_SAVE_SUCCESSFULLY);
		} else {
			toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
		}
	}

	const handleAddCustomer = (customer: Customer, notificationSetting: NotificationSettings) => {
		const newCustomerRow: UserCustomers = {
			id: customer.id,
			name: customer.name,
			customerNumber: customer.customerNumber,
			notificationSetting: notificationSetting,
		};
		const next = [...multiCustomers, newCustomerRow];
		setMultiCustomers(next);
		setIsSelectCustomerOpen(false);
		persistCustomerRelations(next);
	};

	const handleRemoveCustomer = (id: string) => {
		if (multiCustomers.length === 1) return;

		const next = multiCustomers.filter((customer) => customer.id !== id);
		setMultiCustomers(next);
		persistCustomerRelations(next);
	};

	const handleNotificationUpdate = (id: string, notificationSetting: NotificationSettings) => {
		const next = multiCustomers.map((customer) =>
			customer.id === id ? { ...customer, notificationSetting } : customer
		);
		setMultiCustomers(next);
		persistCustomerRelations(next);
	};

	useEffect(() => {
		if (userCustomers && userCustomers.length > 0) {
			setMultiCustomers(userCustomers);
		} else if (customer) {
			setMultiCustomers([
				{ id: customer.id, name: customer.name, customerNumber: customer.customerNumber },
			]);
		}
	}, [userCustomers, customer]);

	const CustomerUserBreadcrumbs: BreadcrumbItem[] = hasCustomerContext
		? [
				...CustomerBreadcrumbs,
				{
					title: customer ? `${customer.name} (${customer.customerNumber})` : "",
					path: customerRoute(customerId || ""),
				},
				{
					title: isEdit ? t("USER") : t("NEW_USER"),
					path: isEdit ? routes.CUSTOMER_USER_EDIT : routes.CUSTOMER_USER_CREATE,
				},
		  ]
		: [
				...CustomerUsersListBreadcrumbs,
				{
					title: customerUser
						? `${customerUser.firstName ?? ""} ${customerUser.lastName ?? ""}`.trim()
						: t("USER"),
					path: routes.CUSTOMER_USER_EDIT,
				},
		  ];

	async function invite(inviteOptionData: InviteOption) {
		const userData = (createdNewUser ?? customerUser) as CustomerUser;
		if (userData.id) {
			const success = await inviteUser({ userId: userData.id, channels: inviteOptionData })
				.then(() => true)
				.catch(() => false);
			if (success) {
				toast.success(t("customer.user.inviteModal.success"));
			} else {
				toast.error(t("customer.user.inviteModal.failure"));
			}
		}
	}

	const closeInviteModal = () => {
		setIsInviteModalOpen(false);
		// The invite modal doubles as the last step of the create flow — once it
		// is dismissed (sent or not) we are done here.
		if (createdNewUser) navigateToUsersList();
	};

	const removeUser = async () => {
		if (customerUser) {
			await deleteCustomerUser(customerUser.id as string).catch(() => undefined);
			navigateToUsersList();
		}
	};

	const onClickRemove = () => {
		const customerConfirmModal: ConfirmModalProps = {
			title: "customer.user.removeUser.title",
			text: t("customer.user.removeUser.text", {
				name: customerUser?.firstName + " " + customerUser?.lastName,
			}),
			acceptButtonText: "customer.user.removeUser.remove",
			rejectButtonText: "CANCEL",
			onReject: () => {},
			onAccept: () => removeUser(),
		};
		modalStore.newModal(customerConfirmModal);
	};

	const actions: ListToolbarAction[] = [
		{
			label: "customer.user.invite",
			onClick: () => setIsInviteModalOpen(true),
		},
		{
			label: "SET_PASSWORD",
			onClick: () => setIsSetPasswordModalOpen(true),
		},
		{
			label: "REMOVE",
			danger: true,
			dividerBefore: true,
			onClick: () => onClickRemove(),
		},
	];

	function addExistingUserToCustomer() {
		const user = customerUsers[0];

		if (!user || !customer) return;

		const confirmModal: ConfirmModalProps = {
			title: "customers.multipleCustomers.add",
			text: `${user.email} kommer läggas till som användare på kund ${customer.name}.`,
			acceptButtonText: "customers.multipleCustomers.addCustomer",
			rejectButtonText: "CANCEL",
			onReject: () => {},
			onAccept: async () => {
				const userData: UpdateCustomerUserRequest = {
					profile: {
						customers: [
							// The search result expands each customer with `name`, which the
							// update endpoint rejects — send only the persisted relation fields.
							...toRelationPayload(user.profile?.customers || []),
							{
								id: customer.id,
								notificationSetting: {
									jobConfirmation: true,
									jobReport: true,
									timeReportSummery: true,
								},
							},
						],
					},
				};
				await updateCustomerUser({ userId: user.id!, update: userData }).catch(() => undefined);

				navigateToCustomerUsers(navigate, customer.id);
			},
		};
		modalStore.newModal(confirmModal);
	}

	const doesOtherUserWithEmailAlreadyExist =
		!isEdit && customerUsers[0] && watch().email === customerUsers[0].email;

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={CustomerUserBreadcrumbs} />
			{/* Same dimensions/position as the DetailPageTabs bar, but without its
			    background and border — the bar's only content here is the "..."
			    menu, so a full-width white bar would just be empty chrome. */}
			<div className="sticky top-2 z-20 mt-4 flex min-h-[48px] items-center justify-end px-2 py-1.5">
				{isEdit && <ListActionsMenu actions={actions} />}
			</div>

			{isLoadingCustomer ? (
				<LoadingIndicator />
			) : (
				<div className="mx-auto mb-16 mt-8 w-full max-w-[1100px]">
					<div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
						{/* Left column — the user's profile, saved with its own button */}
						<form onSubmit={onSubmit} autoComplete="off">
							<FormCard title="BASIC_INFO">
								<div className="flex flex-col gap-4">
									<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
										<AppInputNew
											{...register("firstName")}
											label="FIRST_NAME"
											placeholder="FIRST_NAME"
											errors={formState.errors?.firstName?.message}
											mandatory
										/>
										<AppInputNew
											{...register("lastName")}
											label="LAST_NAME"
											placeholder="LAST_NAME"
											errors={formState.errors?.lastName?.message}
											mandatory
										/>
									</div>

									<div>
										<AppInputNew
											{...register("email")}
											label="EMAIL"
											placeholder="EMAIL"
											errors={formState.errors?.email?.message}
											mandatory
											autoComplete="off"
										/>
										{doesOtherUserWithEmailAlreadyExist && (
											<Alert
												variant="warning"
												skipTranslation
												title="Användaren finns redan"
												description={`Det finns redan en användare med epost ${customerUsers[0].email}.`}
												className="mt-2"
												action={
													<ButtonNew
														variant="neutral"
														className="h-8 px-3 text-xs"
														onClick={addExistingUserToCustomer}
													>
														Lägg till på kunden
													</ButtonNew>
												}
											/>
										)}
									</div>

									<AppInputNew
										{...register("profile.phoneNumber")}
										label="PHONE"
										placeholder={t("PHONE_PLACEHOLDER")}
										errors={formState.errors?.profile?.phoneNumber?.message}
									/>
									<AppInputNew {...register("profile.title")} label="TITLE" />
								</div>

								<div className="mt-6 flex justify-end border-t border-solid border-gray-200 pt-4">
									<ButtonNew type="submit">{isEdit ? t("SAVE") : t("CREATE_USER")}</ButtonNew>
								</div>
							</FormCard>
						</form>

						{/* Right column — customer relations, persisted immediately in edit mode */}
						<FormCard
							title="CUSTOMERS"
							action={
								<AddButton
									label={t("customers.multipleCustomers.add")}
									onClick={() => setIsSelectCustomerOpen(true)}
								/>
							}
						>
							{isEdit && (
								<p className="-mt-3 mb-4 text-xs text-gray-500 max-w-96">
									{t("customers.multipleCustomers.info")}
								</p>
							)}
							{multiCustomers.length > 0 ? (
								<div className="flex flex-col gap-2">
									{multiCustomers.map((customer) => (
										<UserCustomerRow
											key={customer.id}
											customer={customer}
											onRemove={handleRemoveCustomer}
											onNotificationUpdate={handleNotificationUpdate}
											removeDisabled={multiCustomers.length === 1}
										/>
									))}
								</div>
							) : (
								<p className="py-6 text-center text-sm text-gray-500">
									{t("customers.multipleCustomers.noCustomers")}
								</p>
							)}
						</FormCard>
					</div>
				</div>
			)}

			<SelectCustomerDialog
				open={isSelectCustomerOpen}
				onCancel={() => setIsSelectCustomerOpen(false)}
				onConfirmation={handleAddCustomer}
				selectedCustomers={multiCustomers.map((item) => item.id)}
			/>
			{isInviteModalOpen && (
				<InvitationModal
					title={t("customer.user.inviteModal.title", {
						name: (createdNewUser || customerUser)?.firstName,
					})}
					text={t("customer.user.inviteModal.text", {
						email: (createdNewUser || customerUser)?.email,
					})}
					acceptButtonText="SEND"
					rejectButtonText="NOT_NOW_BUTTON"
					onAccept={(invitationData: InviteOption) => {
						invite(invitationData);
						closeInviteModal();
					}}
					onReject={() => closeInviteModal()}
				/>
			)}
			{isEdit && (
				<SetPasswordModal
					isOpen={isSetPasswordModalOpen}
					onClose={() => setIsSetPasswordModalOpen(false)}
					userId={userId}
					userType="customerUser"
				/>
			)}
		</div>
	);
};

export default CustomerUserForm;
